import React, { useEffect, useRef, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';

import { Stack, xcss, Box } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import { getCommentPositionTopOfSection } from '@confluence/comments-util';
import { ReactionsProvider } from '@confluence/comment-context';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { usePageContentId } from '@confluence/page-context';
import {
	useCommentsDataState,
	useActiveCommentsQuery,
	CommentType,
} from '@confluence/comments-data';
import {
	getAnnotationThreadKeys,
	getOpenCommentThreads,
	getVisibleCommentThreads,
	LocationValue,
	useCommentsPanelState,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';
import { useContentState } from '@confluence/content-state/entry-points/useContentState';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import { ContentAppearanceType } from '@confluence/content-appearance';
import { CommentsType } from '@confluence/comments-panel-queries';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';

import { ObjectAddCommentContainer } from './ObjectAddCommentContainer';
import { ObjectCommentsList } from './ObjectCommentsList';
import { ObjectCommentHeader } from './ObjectCommentHeader';

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
	width: '100%',
});

const wrapperStyle = xcss({
	position: 'relative',
	visibility: 'visible',
	minHeight: '50px',
	margin: '0 auto',
	width: '100%',
});

const placeholderStyle = xcss({
	width: '60%',
});

const addCommentContainerStyles = xcss({
	transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	transformOrigin: 'top',
	position: 'sticky',
	bottom: 'space.0',
	zIndex: '1', // Required for create comment editor overflow to show on top of page content
});

const addCommentContainerAnimationHiddenStyles = xcss({
	transform: 'translateY(100%)',
	opacity: 0,
	pointerEvents: 'none',
});

const addCommentContainerAnimationShowStyles = xcss({
	transform: 'translateY(0)',
	opacity: 1,
	pointerEvents: 'auto',
});

type CommentsQueryHookVariables = {
	pageId: string;
	type: CommentsType;
	skip: boolean;
};

type ObjectCommentContainerProps = {
	pageWidthType?: string;
};

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

const ObjectCommentContainerComponent = withFlags(
	({ flags, pageWidthType }: WithFlagsProps & ObjectCommentContainerProps) => {
		const [contentId] = usePageContentId();
		const wrapperRef = useRef<HTMLDivElement>(null);
		const [commentSectionTop, setCommentSectionTop] = useState<number | undefined>(undefined);
		const [isInViewport, setIsInViewport] = useState(false);
		const [allCommentsRendered, setAllCommentsRendered] = useState(false);
		const [hasLoaded, setHasLoaded] = useState(false);
		const [forceLoadComments, setForceLoadComments] = useState(false);
		const [state] = useContentState();
		const pageMode = useGetPageMode();
		const isServer = useRenderServerPlaceholder();
		const { orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap } =
			useCommentsDataState();
		const focusedCommentId = useFocusedCommentIdQueryParam();
		const replyToComment = useReplyToCommentQueryParam();
		const hasScrolledToFocusedComment = useRef(false);
		const hasScrolledToReplyComment = useRef(false);
		const {
			showInlineComments,
			showGeneralComments,
			replyToCommentId,
			commentType,
			currentlySelectedThreadKey,
			location: commentLocation,
		} = useCommentsPanelState();

		const { setCurrentlySelectedThreadKey, setReplyToCommentId } = useCommentsPanelActions();

		// Set up IntersectionObserver
		useEffect(() => {
			if (isServer) {
				return;
			}

			// eslint-disable-next-line no-restricted-syntax
			const observer = new IntersectionObserver(
				([entry]) => {
					const isIntersecting = entry.isIntersecting;
					if (isIntersecting) {
						setIsInViewport(isIntersecting);
					}
				},
				{
					root: null,
					rootMargin: '10px',
					threshold: 0.1,
				},
			);

			if (wrapperRef.current) {
				observer.observe(wrapperRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}, [isServer, pageMode, state.isContentReady]);

		// Add MutationObserver to detect when comments are actually rendered
		useEffect(() => {
			if (isServer) {
				return;
			}

			const observer = new MutationObserver(() => {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const commentElement = document.getElementById(commentElementId);
				if (commentElement) {
					setAllCommentsRendered(true);
					observer.disconnect(); // Disconnect once we find the element
				} else {
					setAllCommentsRendered(false); // Reset if element not found
				}
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});

			// Reset allCommentsRendered when unmounting or changing thread key
			return () => {
				setAllCommentsRendered(false);
				observer.disconnect();
			};
		}, [currentlySelectedThreadKey, isServer]);

		// Query setup - skip if not forcing load and not in viewport and not loaded yet
		const commentsQueryHook = useActiveCommentsQuery;
		const commentsQueryHookVariables: CommentsQueryHookVariables = {
			pageId: contentId || '',
			skip: !forceLoadComments && !isInViewport,
			type: CommentsType.FOOTER,
		};

		const { loading, error, data } = commentsQueryHook(commentsQueryHookVariables);

		// Update loaded state when data is received
		useEffect(() => {
			if (data && !hasLoaded) {
				setHasLoaded(true);
				setForceLoadComments(false);
			}
		}, [data, hasLoaded]);

		// Handle scrolling after comments are loaded
		useEffect(() => {
			if (hasLoaded && commentType === CommentType.GENERAL && allCommentsRendered) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (isCommentExists) {
					const rect = isCommentExists.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);
					if (!commentIsInViewport) {
						isCommentExists.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
							inline: 'center',
						});
					}
				}
			}
		}, [hasLoaded, commentType, currentlySelectedThreadKey, allCommentsRendered]);

		useEffect(() => {
			if (commentType === CommentType.GENERAL) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (!isCommentExists) {
					setForceLoadComments(true);
				}
			}
		}, [currentlySelectedThreadKey, commentType]);

		// Handle editor resize
		useEffect(() => {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			if (editorTextArea) {
				const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
					if (fg('confluence_footer_comment_styling_change')) {
						const newTop = getCommentPositionTopOfSection(entries);
						if (fg('confluence_footer_comment_styling_fix_flicker')) {
							flushSync(() => {
								newTop && setCommentSectionTop(newTop);
							});
						} else {
							newTop && setCommentSectionTop(newTop);
						}
					}
				});
				resizeObserver.observe(editorTextArea);
				return () => resizeObserver.disconnect();
			}
		}, []);

		// Handle scrolling to focused comment when data is loaded
		useEffect(() => {
			if (
				hasLoaded &&
				(focusedCommentId || replyToComment) &&
				allCommentsRendered &&
				!hasScrolledToFocusedComment.current &&
				!hasScrolledToReplyComment.current
			) {
				const commentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-container`;
				const replyCommentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-replies`;

				const commentElement = document.getElementById(commentElementId);
				const replyCommentElement = document.getElementById(replyCommentElementId);
				const element = commentElement || replyCommentElement;
				if (element) {
					if (replyToComment) {
						setCurrentlySelectedThreadKey(replyToComment);
						setReplyToCommentId({
							parentCommentId: replyToComment,
							location: LocationValue.FOOTER,
						});
						hasScrolledToReplyComment.current = true;
						return;
					}
					const rect = element.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);
					if (!commentIsInViewport) {
						element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
						hasScrolledToFocusedComment.current = true;
						setCurrentlySelectedThreadKey(focusedCommentId);
					}
				}
			}
		}, [
			hasLoaded,
			focusedCommentId,
			replyToComment,
			allCommentsRendered,
			setCurrentlySelectedThreadKey,
			setReplyToCommentId,
		]);

		// Reset scroll tracking when focusedCommentId or replyToComment changes
		useEffect(() => {
			hasScrolledToFocusedComment.current = false;
			hasScrolledToReplyComment.current = false;
		}, [focusedCommentId, replyToComment]);

		// Force load comments if focusedCommentId is present
		useEffect(() => {
			if ((focusedCommentId || replyToComment) && !hasLoaded) {
				setForceLoadComments(true);
			}
		}, [focusedCommentId, replyToComment, hasLoaded]);

		if (error) {
			return null;
		}

		const commentThreads = useMemo(() => {
			if (!hasLoaded) {
				return [];
			}

			return fg('confluence_frontend_immediate_comment_removal')
				? getVisibleCommentThreads({
						commentsDataMap,
						includeGeneralComments: showGeneralComments,
						includeInlineComments: false,
						orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
					})
				: getOpenCommentThreads({
						commentsDataMap,
						showInlineComments,
						showGeneralComments,
						location: LocationValue.FOOTER,
						orderedActiveAnnotationIdList,
						removedThreadsMap,
					});
		}, [
			hasLoaded,
			commentsDataMap,
			orderedActiveAnnotationIdList,
			removedThreadsMap,
			showInlineComments,
			showGeneralComments,
		]);

		const getMaxWidth = () => {
			if (pageWidthType === undefined) {
				return '100%';
			}
			// pageWidthType is a string like '"default"'
			return JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT ? '1000px' : 'unset';
		};

		const hasComments = commentThreads.length > 0;

		// Show placeholder when:
		// For View mode: show when content is ready and comments are not loaded
		// For Live mode: show when in viewport and loading
		// Always show during SSR
		const shouldShowPlaceholder =
			pageMode === PageMode.VIEW ? state.isContentReady && !hasLoaded : isInViewport && loading;

		// Show comments when loaded, regardless of viewport
		const shouldShowComments = hasLoaded;

		// Only render the Box component if we're not in view mode or content is ready
		if (pageMode === PageMode.VIEW && !state.isContentReady) {
			return null;
		}

		return (
			<Box
				ref={wrapperRef}
				xcss={wrapperStyle}
				testId="object-comment-wrapper"
				style={{
					top: commentSectionTop,
					maxWidth: getMaxWidth(),
				}}
			>
				{shouldShowPlaceholder && (
					<Box xcss={placeholderStyle}>
						<CommentPlaceholder showPlaceholder />
					</Box>
				)}
				{shouldShowComments && (
					<ReactionsProvider contentId={contentId ?? ''}>
						{hasComments && <ObjectCommentHeader />}
						<Stack xcss={commentPanelContainerStyles}>
							<ObjectCommentsList
								commentThreads={commentThreads}
								supportedTopLevelActions={['edit', 'resolve', 'delete']}
								flags={flags}
								onAllCommentsRendered={() => setAllCommentsRendered(true)}
							/>
						</Stack>
						<Box
							xcss={[
								addCommentContainerStyles,
								!!replyToCommentId && commentLocation === LocationValue.FOOTER
									? addCommentContainerAnimationHiddenStyles
									: addCommentContainerAnimationShowStyles,
							]}
						>
							{!shouldShowPlaceholder && <ObjectAddCommentContainer showReactions={!hasComments} />}
						</Box>
					</ReactionsProvider>
				)}
			</Box>
		);
	},
);

export const ObjectCommentContainer = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(ObjectCommentContainerComponent);
